import React, { useState } from 'react';
import { Button, IconButton } from '@carbon/react';
import {
  Launch,
  Close,
  OpenPanelRight,
  OpenPanelBottom,
} from '@carbon/react/icons';
import styles from './doc-image-panel.module.scss';

// §3.13 layout modes (R20 keeps both in scope): the doc image can sit as a
// side third-column (the default/wide layout) or stacked below the reconcile
// content for narrower/laptop viewports.
export type DocImageLayout = 'side' | 'stacked';

const DEFAULT_LAYOUTS: DocImageLayout[] = ['side', 'stacked'];

interface Props {
  documentId: number;
  documentName?: string;
  onClose: () => void;
  // Layout is self-contained (uncontrolled): the panel owns the mode, seeded by
  // `initialLayout`, and reports each change through `onLayoutChange` so the host
  // can reposition the panel (side third-column vs. stacked strip). Restrict the
  // in-header toggle with `availableLayouts`; a single entry hides it entirely.
  initialLayout?: DocImageLayout;
  availableLayouts?: DocImageLayout[];
  onLayoutChange?: (layout: DocImageLayout) => void;
  // Lets the host pin the panel's size (e.g. the stacked strip's height once the
  // user drags its resize divider); merged onto the panel root.
  style?: React.CSSProperties;
}

// Document Image View (§3.13): the third panel on the Match Review reconcile
// screen, showing the source document image alongside the entity list and Entity
// Consolidation. The reconcile is scoped to one document, so it renders that
// document's image; "Open in new tab" undocks it into a separate browser window
// (the sandboxed download/undock caveats don't apply — this is a plain link).
const DocImagePanel: React.FC<Props> = ({
  documentId,
  documentName,
  onClose,
  initialLayout = 'side',
  availableLayouts = DEFAULT_LAYOUTS,
  onLayoutChange,
  style,
}) => {
  const [layout, setLayout] = useState<DocImageLayout>(initialLayout);
  const src = `/documents/${documentId}/image`;

  const changeLayout = (next: DocImageLayout) => {
    if (next === layout) return;
    setLayout(next);
    onLayoutChange?.(next);
  };

  const showLayoutToggle = availableLayouts.length > 1;

  return (
    <div
      className={`${styles.panel} ${styles[layout]}`}
      data-layout={layout}
      style={style}
    >
      <div className={styles.header}>
        <h4 className={styles.heading}>
          {documentName || `Document #${documentId}`}
        </h4>
        <div className={styles.actions}>
          {showLayoutToggle && (
            <div
              className={styles.layoutToggle}
              role="group"
              aria-label="Document image layout"
            >
              {availableLayouts.includes('side') && (
                <IconButton
                  kind="ghost"
                  size="sm"
                  label="Side-by-side layout"
                  isSelected={layout === 'side'}
                  onClick={() => changeLayout('side')}
                >
                  <OpenPanelRight />
                </IconButton>
              )}
              {availableLayouts.includes('stacked') && (
                <IconButton
                  kind="ghost"
                  size="sm"
                  label="Stacked layout"
                  isSelected={layout === 'stacked'}
                  onClick={() => changeLayout('stacked')}
                >
                  <OpenPanelBottom />
                </IconButton>
              )}
            </div>
          )}
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Launch}
            iconDescription="Open in new tab"
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            as="a"
          />
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Close}
            iconDescription="Close document image"
            onClick={onClose}
          />
        </div>
      </div>
      <div className={styles.content}>
        <iframe src={src} className={styles.iframe} title="Document image" />
      </div>
    </div>
  );
};

export default DocImagePanel;
